/**
 * 🎯 SpeakPipe Override Utility
 * דריסה חזקה של הגדרות SpeakPipe באמצעות JavaScript
 */

export class SpeakPipeOverride {
  private static readonly OVERRIDE_INTERVAL = 100; // בדיקה כל 100ms
  private static readonly TARGET_TOP_DESKTOP = '48%'; // מיקום נמוך מאוד למחשב
  private static readonly TARGET_TOP_MOBILE = '80%'; // מיקום נמוך למובייל - לא בתחתית
  private static readonly TARGET_BOTTOM_MOBILE = 'auto'; // לא משתמש ב-bottom למובייל
  private static readonly TARGET_Z_INDEX = '100'; // z-index נמוך
  
  /**
   * בדיקה אם המכשיר הוא מובייל או טאבלט
   */
  private static isMobileOrTablet(): boolean {
    return window.innerWidth <= 1024 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }
  
  /**
   * קבלת המיקום המתאים לפי סוג המכשיר
   */
  private static getTargetPosition(): { top: string; bottom: string; left: string; right: string; transform: string; transformOrigin: string } {
    const isRTL = document.documentElement.dir === 'rtl';
    
    if (this.isMobileOrTablet()) {
      return {
        top: this.TARGET_TOP_MOBILE,
        bottom: this.TARGET_BOTTOM_MOBILE,
        left: isRTL ? '0px' : 'auto', // עברית בצד שמאל, אנגלית בצד ימין
        right: isRTL ? 'auto' : '0px', // עברית בצד שמאל, אנגלית בצד ימין
        transform: isRTL ? 'rotate(90deg) translateX(-50%)' : 'rotate(-90deg) translateX(50%)', // הפוך את הטרנספורמציה
        transformOrigin: isRTL ? 'bottom left' : 'bottom right' // הפוך את נקודת המוצא
      };
    }
    return {
      top: this.TARGET_TOP_DESKTOP,
      bottom: 'auto',
      left: isRTL ? '0px' : 'auto', // עברית בצד שמאל, אנגלית בצד ימין
      right: isRTL ? 'auto' : '0px', // עברית בצד שמאל, אנגלית בצד ימין
      transform: isRTL ? 'rotate(90deg) translateX(-50%)' : 'rotate(-90deg) translateX(50%)', // הפוך את הטרנספורמציה
      transformOrigin: isRTL ? 'bottom left' : 'bottom right' // הפוך את נקודת המוצא
    };
  }
  
  /**
   * הפעלת הדריסה החזקה של SpeakPipe
   */
  static startOverride(): void {
    console.log('🎯 Starting SpeakPipe override...');
    
    // הפעלה מיידית
    this.forceOverride();
    
    // הפעלה מתמשכת עם interval
    setInterval(() => {
      this.forceOverride();
    }, this.OVERRIDE_INTERVAL);
    
    // הפעלה נוספת אחרי טעינת הדף
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.forceOverride();
      }, 1000);
    });
    
    // הפעלה נוספת אחרי שינויי DOM
    this.observeDOMChanges();
    
    // הפעלה נוספת אחרי שינוי גודל החלון
    window.addEventListener('resize', () => {
      setTimeout(() => {
        this.forceOverride();
      }, 100);
    });
  }
  
  /**
   * דריסה חזקה של כל אלמנטי SpeakPipe
   */
  private static forceOverride(): void {
    // דריסה חזקה של כפתור הצד
    this.overrideSideButton();
    
    // דריסה חזקה של כל הכפתורים
    this.overrideAllButtons();
    
    // דריסה חזקה של iframe
    this.overrideIframes();
  }
  
  /**
   * דריסה חזקה של כפתור הצד
   */
  private static overrideSideButton(): void {
    const selectors = [
      '#-speakpipe-widget-side-button.speakpipe-button.speakpipe-btn-middle-right',
      '.speakpipe-button.speakpipe-btn-middle-right',
      '[class*="speakpipe"][class*="button"][class*="middle"]',
      'a[href="#"].speakpipe-button'
    ];
    
    const position = this.getTargetPosition();
    
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        if (element instanceof HTMLElement) {
          // דריסה חזקה של המיקום
          element.style.setProperty('top', position.top, 'important');
          element.style.setProperty('bottom', position.bottom, 'important');
          element.style.setProperty('left', position.left, 'important');
          element.style.setProperty('right', position.right, 'important');
          element.style.setProperty('transform', position.transform, 'important');
          element.style.setProperty('transform-origin', position.transformOrigin, 'important');
          element.style.setProperty('position', 'fixed', 'important');
          element.style.setProperty('z-index', this.TARGET_Z_INDEX, 'important');
          
          // דריסה חזקה של המיקום עם CSS inline
          element.style.cssText += `
            top: ${position.top} !important;
            bottom: ${position.bottom} !important;
            left: ${position.left} !important;
            right: ${position.right} !important;
            transform: ${position.transform} !important;
            transform-origin: ${position.transformOrigin} !important;
            position: fixed !important;
            z-index: ${this.TARGET_Z_INDEX} !important;
          `;
          
          // הוספת CSS class עם !important
          element.classList.add('speakpipe-override');
        }
      });
    });
  }
  
  /**
   * דריסה חזקה של כל הכפתורים
   */
  private static overrideAllButtons(): void {
    const buttons = document.querySelectorAll('.speakpipe-button, [class*="speakpipe"][class*="button"]');
    const position = this.getTargetPosition();
    
    buttons.forEach(button => {
      if (button instanceof HTMLElement) {
        button.style.setProperty('top', position.top, 'important');
        button.style.setProperty('bottom', position.bottom, 'important');
        button.style.setProperty('left', position.left, 'important');
        button.style.setProperty('right', position.right, 'important');
        button.style.setProperty('transform', position.transform, 'important');
        button.style.setProperty('transform-origin', position.transformOrigin, 'important');
        button.style.setProperty('position', 'fixed', 'important');
        button.style.setProperty('z-index', this.TARGET_Z_INDEX, 'important');
      }
    });
  }
  
  /**
   * דריסה חזקה של iframe
   */
  private static overrideIframes(): void {
    const iframes = document.querySelectorAll('iframe[src*="speakpipe"]');
    iframes.forEach(iframe => {
      if (iframe instanceof HTMLIFrameElement) {
        iframe.style.setProperty('z-index', '10000', 'important');
      }
    });
  }
  
  /**
   * מעקב אחרי שינויי DOM
   */
  private static observeDOMChanges(): void {
    if (window.MutationObserver) {
      const observer = new MutationObserver((mutations) => {
        let shouldOverride = false;
        
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node instanceof HTMLElement) {
                if (node.classList.contains('speakpipe-button') || 
                    node.id === '-speakpipe-widget-side-button' ||
                    node.querySelector('.speakpipe-button')) {
                  shouldOverride = true;
                }
              }
            });
          }
        });
        
        if (shouldOverride) {
          setTimeout(() => {
            this.forceOverride();
          }, 100);
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }
  
  /**
   * הוספת CSS override נוסף
   */
  static injectOverrideCSS(): void {
    const style = document.createElement('style');
    style.id = 'speakpipe-override-css';
    style.textContent = `
      /* דריסה חזקה של SpeakPipe למחשב */
      @media (min-width: 1025px) {
        #-speakpipe-widget-side-button.speakpipe-button.speakpipe-btn-middle-right,
        .speakpipe-button.speakpipe-btn-middle-right,
        [class*="speakpipe"][class*="button"][class*="middle"] {
          top: ${this.TARGET_TOP_DESKTOP} !important;
          bottom: auto !important;
          position: fixed !important;
          z-index: ${this.TARGET_Z_INDEX} !important;
        }
        
        /* RTL - מחשב (עברית בצד שמאל) */
        [dir='rtl'] #-speakpipe-widget-side-button.speakpipe-btn-middle-right {
          left: 0px !important;
          right: auto !important;
          transform: rotate(90deg) translateX(-50%) !important;
          transform-origin: bottom left !important;
        }
        
        /* LTR - מחשב (אנגלית בצד ימין) */
        [dir='ltr'] #-speakpipe-widget-side-button.speakpipe-button {
          right: 0px !important;
          left: auto !important;
          transform: rotate(-90deg) translateX(50%) !important;
          transform-origin: bottom right !important;
        }
      }
      
      /* דריסה חזקה של SpeakPipe למובייל וטאבלט */
      @media (max-width: 1024px) {
        #-speakpipe-widget-side-button.speakpipe-button.speakpipe-btn-middle-right,
        .speakpipe-button.speakpipe-btn-middle-right,
        [class*="speakpipe"][class*="button"][class*="middle"] {
          top: ${this.TARGET_TOP_MOBILE} !important;
          bottom: ${this.TARGET_BOTTOM_MOBILE} !important;
          position: fixed !important;
          z-index: ${this.TARGET_Z_INDEX} !important;
        }
        
        /* RTL - מובייל (עברית בצד שמאל) */
        [dir='rtl'] #-speakpipe-widget-side-button.speakpipe-btn-middle-right {
          left: 0px !important;
          right: auto !important;
          top: ${this.TARGET_TOP_MOBILE} !important;
          bottom: auto !important;
          transform: rotate(90deg) translateX(-50%) !important;
          transform-origin: bottom left !important;
        }
        
        /* LTR - מובייל (אנגלית בצד ימין) */
        [dir='ltr'] #-speakpipe-widget-side-button.speakpipe-button {
          right: 0px !important;
          left: auto !important;
          top: ${this.TARGET_TOP_MOBILE} !important;
          bottom: auto !important;
          transform: rotate(-90deg) translateX(50%) !important;
          transform-origin: bottom right !important;
        }
      }
    `;
    
    // הסרת CSS קיים אם יש
    const existingStyle = document.getElementById('speakpipe-override-css');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    document.head.appendChild(style);
  }
}

// הפעלה אוטומטית
if (typeof window !== 'undefined') {
  // הפעלה אחרי טעינת הדף
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      SpeakPipeOverride.injectOverrideCSS();
      SpeakPipeOverride.startOverride();
    });
  } else {
    SpeakPipeOverride.injectOverrideCSS();
    SpeakPipeOverride.startOverride();
  }
}
